/**
 * German-notation formatting for the numbers the apps state next to each other: amounts, rates,
 * months and dates. Everything is Intl-based, so no date library is pulled in for it.
 *
 * Unlike the `$filters` of filters.ts, which render an absent value as an empty string, these
 * treat an absent value as zero - they are meant for ledgers and summaries where a missing
 * number still occupies its place.
 */

const currencyFormat = new Intl.NumberFormat('de-DE', {
	style: 'currency',
	currency: 'EUR',
	minimumFractionDigits: 2,
	maximumFractionDigits: 2,
});

const wholeCurrencyFormat = new Intl.NumberFormat('de-DE', {
	style: 'currency',
	currency: 'EUR',
	minimumFractionDigits: 0,
	maximumFractionDigits: 0,
});

const monthFormat = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });
const shortMonthFormat = new Intl.DateTimeFormat('de-DE', { month: 'short' });
const dateFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
const weekdayDateFormat = new Intl.DateTimeFormat('de-DE', {
	weekday: 'short',
	day: '2-digit',
	month: '2-digit',
	year: 'numeric',
});

// Decimals come off the API as strings, so everything is coerced before it is formatted.
export function toNumber(value: number | string | null | undefined): number {
	if (value === null || value === undefined || value === '') return 0;
	const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
	return Number.isNaN(parsed) ? 0 : parsed;
}

// 1234.5 to 1.234,50 €, or rounded to whole euros with `whole` for numbers read side by side.
export function formatCurrency(value: number | string | null | undefined, whole = false): string {
	return (whole ? wholeCurrencyFormat : currencyFormat).format(toNumber(value));
}

// Interest and repayment rates, e.g. "3,45 %". Trailing zeros are dropped.
export function formatPercent(value: number | string | null | undefined, maxDigits = 3): string {
	return `${new Intl.NumberFormat('de-DE', { maximumFractionDigits: maxDigits }).format(toNumber(value))} %`;
}

// Midday, so no time zone can push the date onto the day or month before.
function dateOf(day: string): Date {
	return new Date(`${day}T12:00:00`);
}

// `2026-05` to `Mai 2026`, a month as the laravel states it in `YYYY-MM`.
export function formatMonth(period: string): string {
	return monthFormat.format(dateOf(`${period}-01`));
}

// `2026-05` to `Mai`, just the month.
export function formatMonthShort(period: string): string {
	return shortMonthFormat.format(dateOf(`${period}-01`));
}

// `2032-03-12` to `12.03.2032`, the notation every date is written in.
export function formatDate(date: string): string {
	return dateFormat.format(dateOf(date));
}

// `2026-10-13` to `Di., 13.10.2026`, a day picked from a list, where the weekday finds it faster.
export function formatDateWithWeekday(date: string): string {
	return weekdayDateFormat.format(dateOf(date));
}
